import React, { useState } from 'react';
import SkeletonRow from './SkeletonRow';

const UI = {
  spacing: { xs: 6, sm: 8, md: 12, lg: 16, xl: 24 },
  radius: { sm: 6, md: 8, lg: 12 },
  colors: {
    primary: "#0F172A",
    secondary: "#334155",
    muted: "#64748B",
    border: "#E2E8F0",
    bg: "#F8FAFC",
    success: "#059669",
    warning: "#D97706",
    danger: "#DC2626",
    info: "#3B82F6"
  }
};

const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>;
  return <span style={{ marginLeft: 4 }}>{sortDir === 1 ? "↑" : "↓"}</span>;
};

const Table = ({
  columns,
  data,
  selectedIds = new Set(),
  onSelect,
  onSelectAll,
  onSort,
  sortField,
  sortDir,
  loading = false,
  onRowClick,
  selectable = true
}) => {
  const [focusedRowIndex, setFocusedRowIndex] = useState(-1);
  
  const allSelected = data.length > 0 && data.every(row => selectedIds.has(row.id));
  const someSelected = data.some(row => selectedIds.has(row.id));
  
  const handleKeyDown = (e, row, index) => {
    if (e.key === 'Enter' && onRowClick) {
      onRowClick(row);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const newIndex = Math.min(index + 1, data.length - 1);
      setFocusedRowIndex(newIndex);
      document.getElementById(`row-${data[newIndex].id}`)?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const newIndex = Math.max(index - 1, 0);
      setFocusedRowIndex(newIndex);
      document.getElementById(`row-${data[newIndex].id}`)?.focus();
    }
  };
  
  if (loading) {
    return (
      <div style={{
        background: UI.colors.white,
        border: `1px solid ${UI.colors.border}`,
        borderRadius: UI.radius.lg,
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {selectable && <th style={{ width: 44, padding: UI.spacing.md }} />}
                {columns.map((col, idx) => (
                  <th key={idx} style={{ width: col.width, padding: UI.spacing.md, textAlign: col.align || 'left' }}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...Array(8)].map((_, i) => (
                <SkeletonRow key={i} columnCount={columns.length + (selectable ? 1 : 0)} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  
  if (data.length === 0) {
    return (
      <div style={{
        background: UI.colors.white,
        border: `1px solid ${UI.colors.border}`,
        borderRadius: UI.radius.lg,
        padding: UI.spacing.xl,
        textAlign: 'center',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        color: UI.colors.muted
      }}>
        No data available
      </div>
    );
  }
  
  return (
    <div style={{
      background: UI.colors.white,
      border: `1px solid ${UI.colors.border}`,
      borderRadius: UI.radius.lg,
      overflow: 'hidden'
    }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: UI.colors.bg, borderBottom: `1px solid ${UI.colors.border}` }}>
            <tr>
              {selectable && (
                <th style={{ width: 44, padding: UI.spacing.md, textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = someSelected && !allSelected;
                    }}
                    onChange={(e) => onSelectAll?.(e.target.checked, data.map(row => row.id))}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
              )}
              {columns.map((col, idx) => (
                <th
                  key={idx}
                  style={{
                    width: col.width,
                    padding: UI.spacing.md,
                    textAlign: col.align || 'left',
                    cursor: col.sortable ? 'pointer' : 'default'
                  }}
                  onClick={() => col.sortable && onSort?.(col.key)}
                >
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 12,
                    fontWeight: 600,
                    color: UI.colors.secondary
                  }}>
                    {col.label}
                    {col.sortable && <SortIcon field={col.key} sortField={sortField} sortDir={sortDir} />}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr
                id={`row-${row.id}`}
                key={row.id}
                onClick={() => onRowClick?.(row)}
                onKeyDown={(e) => handleKeyDown(e, row, index)}
                tabIndex={0}
                style={{
                  background: selectedIds.has(row.id) ? '#F0FDF4' : UI.colors.white,
                  borderBottom: `1px solid ${UI.colors.border}`,
                  cursor: onRowClick ? 'pointer' : 'default',
                  transition: 'background 0.15s ease',
                  outline: focusedRowIndex === index ? `2px solid ${UI.colors.info}` : 'none'
                }}
                onMouseEnter={(e) => {
                  if (!selectedIds.has(row.id)) e.currentTarget.style.background = UI.colors.bg;
                }}
                onMouseLeave={(e) => {
                  if (!selectedIds.has(row.id)) e.currentTarget.style.background = UI.colors.white;
                }}
              >
                {selectable && (
                  <td style={{ padding: UI.spacing.md, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.id)}
                      onChange={() => onSelect?.(row.id)}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                )}
                {columns.map((col, colIdx) => (
                  <td
                    key={colIdx}
                    style={{
                      padding: UI.spacing.md,
                      textAlign: col.align || 'left',
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 13,
                      color: UI.colors.secondary
                    }}
                  >
                    {col.render ? col.render(row[col.key], row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Table;